<template>
    <div class="content-blocks px-6 pb-6 pt-2">
        <h4 class="mb-4 text-lg font-bold text-gray-600 dark:text-gray-300">
            Content Blocks
        </h4>
        <!-- Content Block -->
        <template v-if="blocks.length">
            <draggable :list="blocks"
                       item-key="uuid"
                       @change="$emit('blocks-changed')"
                       handle=".block-handle"
            >
                <template #item="{element, index}">
                    <div class="content-block mb-4 bg-white dark:bg-gray-800 rounded-lg">
                        <div
                            class="flex items-center px-3 py-2 text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 text-right border dark:border-gray-500 border-gray-200 rounded-md">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-3 cursor-move block-handle"
                                 viewBox="0 0 512 512" fill="currentColor">
                                <path
                                    d="M512 256c0 6.797-2.891 13.28-7.938 17.84l-80 72C419.6 349.9 413.8 352 408 352c-3.312 0-6.625-.6875-9.766-2.078C389.6 346.1 384 337.5 384 328V288h-96v96l40-.0013c9.484 0 18.06 5.578 21.92 14.23s2.25 18.78-4.078 25.83l-72 80C269.3 509.1 262.8 512 255.1 512s-13.28-2.89-17.84-7.937l-71.1-80c-6.328-7.047-7.938-17.17-4.078-25.83s12.44-14.23 21.92-14.23l39.1 .0013V288H128v40c0 9.484-5.578 18.06-14.23 21.92C110.6 351.3 107.3 352 104 352c-5.812 0-11.56-2.109-16.06-6.156l-80-72C2.891 269.3 0 262.8 0 256s2.891-13.28 7.938-17.84l80-72C95 159.8 105.1 158.3 113.8 162.1C122.4 165.9 128 174.5 128 184V224h95.1V128l-39.1-.0013c-9.484 0-18.06-5.578-21.92-14.23S159.8 94.99 166.2 87.94l71.1-80c9.125-10.09 26.56-10.09 35.69 0l72 80c6.328 7.047 7.938 17.17 4.078 25.83s-12.44 14.23-21.92 14.23l-40 .0013V224H384V184c0-9.484 5.578-18.06 14.23-21.92c8.656-3.812 18.77-2.266 25.83 4.078l80 72C509.1 242.7 512 249.2 512 256z"/>
                            </svg>

                            <h4 class="text-sm font-semibold text-gray-600 dark:text-gray-300">
                                {{ element.name }}
                            </h4>
                            <button @click.prevent="saveCustomBlock(element)"
                                    class="flex items-center justify-between p-2 ml-auto font-medium leading-5 text-white transition-colors duration-150 bg-gray-600 border border-transparent rounded-lg active:bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-400 focus:outline-none focus:shadow-outline-gray"
                                    title="Save Block to library"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M17 16v2a2 2 0 01-2 2H5a2 2 0 01-2-2v-7a2 2 0 012-2h2m3-4H9a2 2 0 00-2 2v7a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-1m-1 4l-3 3m0 0l-3-3m3 3V3" />
                                </svg>
                            </button>
                            <button @click.prevent="openBlockPreviewModal(element)"
                                    class="flex items-center justify-between p-2 ml-1 font-medium leading-5 text-white transition-colors duration-150 bg-gray-600 border border-transparent rounded-lg active:bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-400 focus:outline-none focus:shadow-outline-gray"
                                    title="Preview Element"
                            >
                                <svg v-show="!element.show" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4"
                                     viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                                    <path fill-rule="evenodd"
                                          d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                                          clip-rule="evenodd"/>
                                </svg>
                                <svg v-show="element.show" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4"
                                     viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd"
                                          d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z"
                                          clip-rule="evenodd"/>
                                    <path
                                        d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z"/>
                                </svg>
                            </button>
                            <button @click.prevent="editBlock(element)"
                                    class="flex items-center justify-between p-2 ml-1 font-medium leading-5 text-white transition-colors duration-150 bg-gray-600 border border-transparent rounded-lg active:bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-400 focus:outline-none focus:shadow-outline-gray"
                                    title="Edit Element"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20"
                                     fill="currentColor">
                                    <path
                                        d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/>
                                </svg>
                            </button>
                            <button @click.prevent="cloneBlock(element)"
                                    class="flex items-center justify-between p-2 ml-1 font-medium leading-5 text-white transition-colors duration-150 bg-gray-600 border border-transparent rounded-lg active:bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-400 focus:outline-none focus:shadow-outline-gray"
                                    title="Clone Element"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20"
                                     fill="currentColor">
                                    <path
                                        d="M9 2a2 2 0 00-2 2v8a2 2 0 002 2h6a2 2 0 002-2V6.414A2 2 0 0016.414 5L14 2.586A2 2 0 0012.586 2H9z"/>
                                    <path d="M3 8a2 2 0 012-2v10h8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"/>
                                </svg>
                            </button>
                            <button @click.prevent="deleteBlock(index)"
                                    class="flex items-center justify-between p-2 ml-1 font-medium leading-5 text-white transition-colors duration-150 bg-gray-600 border border-transparent rounded-lg active:bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-400 focus:outline-none focus:shadow-outline-gray"
                                    title="Delete Element"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none"
                                     viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </template>
            </draggable>
        </template>

        <button @click="openBlockTypeModal"
                class="w-full flex items-center justify-center px-4 py-2 text-sm font-medium leading-5 text-gray-600 transition-colors duration-150 bg-transparent border border-red-600 rounded-lg active:text-gray-600 hover:text-gray-700 focus:outline-none focus:shadow-outline-red">
            <span class="mr-2" aria-hidden="true">+</span>
            <span>Element Block</span>
        </button>

        <teleport to="body">
            <quote-block-save-modal v-if="showBlockSaveModal"
                                    :is-modal-open="showBlockSaveModal"
                                    :source-block-type-element="sourceBlockTypeElement"
                                    :quote="quote"
                                    @close="showBlockSaveModal = false"
            ></quote-block-save-modal>
        </teleport>
    </div>
</template>

<script>
import {useModelWrapper} from "@/includes/modelWrapper";
import Draggable from "vuedraggable";
import {v4 as uuidv4} from 'uuid';
import QuoteBlockSaveModal from "@/Pages/Admin/Quote/Partials/QuoteBlockSaveModal";
import {ref} from "vue";

export default {
    name: "QuoteSectionContentEditor",
    components: {
        QuoteBlockSaveModal,
        Draggable,
    },
    props: {
        client: {
            type: Object,
            required: false,
        },
        quote: {
            type: Object,
            required: false,
        },
        blocksModel: {
            type: Array,
            required: true,
        }
    },
    setup(props, {emit}) {
        const blocks = useModelWrapper(props, emit, 'blocksModel');

        const blocksChanged = () => {
            emit('blocks-changed')
        }

        const fixBlockRelativePaths = (string, uuid) => {
            return props.client
                ? string.replace(/{ELEMENT_PATH}/g, '/quotes/' + props.client.id + '/' + props.quote.id + '/' + uuid)
                : string.replace(/{ELEMENT_PATH}/g, '/quotes/templates/' + props.quote.id + '/' + uuid);
        }

        const addBlock = ({id, name, source_folder_name, quote_block_type_source_folder_name, styles, styles_pdf, html, preview_image_url}) => {
            let uuid = uuidv4();
            blocks.value.push({
                uuid: uuid,
                name,
                source_folder_name,
                quote_block_type_source_folder_name,
                styles: fixBlockRelativePaths(styles, uuid),
                styles_pdf: fixBlockRelativePaths(styles_pdf, uuid),
                html: fixBlockRelativePaths(html, uuid),
                preview_image_url
            });

            let saveRoute;
            if (props.client) {
                saveRoute = route('admin.quote.block.media.update', {
                    client: props.client.id,
                    quote: props.quote.id,
                    blockUuid: uuid,
                });
            } else {
                saveRoute = route('admin.quote-template.block.media.update', {
                    quote_template: props.quote.id,
                    blockUuid: uuid,
                });
            }

            if (source_folder_name) {
                axios.patch(saveRoute, {
                    operation: "blockTypeAdded",
                    source_folder_name: source_folder_name,
                });
            } else if (quote_block_type_source_folder_name) {
                axios.patch(saveRoute, {
                    operation: "blockAdded",
                    quote_block_id: id,
                });
            }

            blocksChanged();
        };

        const openBlockTypeModal = () => {
            emit('open-block-type-modal', addBlock)
        };

        const openBlockPreviewModal = (element) => {
            emit('open-block-preview-modal', element);
        };

        const editBlock = (element) => {
            emit('open-block-edit-modal', {element, callback: updateBlockContent});
        };

        const updateBlockContent = (element, html) => {
            const blockIndex = props.blocksModel.findIndex((block) => block.uuid === element.uuid);
            if (blockIndex !== -1) {
                blocks.value[blockIndex].html = html;
            }
        }

        const deleteBlock = (blockIndex) => {
            let saveRoute;
            if (props.client) {
                saveRoute = route('admin.quote.block.media.update', {
                    client: props.client.id,
                    quote: props.quote.id,
                    blockUuid: blocks.value[blockIndex].uuid,
                });
            } else {
                saveRoute = route('admin.quote-template.block.media.update', {
                    quote_template: props.quote.id,
                    blockUuid: blocks.value[blockIndex].uuid,
                });
            }

            axios.patch(saveRoute, {
                operation: "blockDeleted",
            });

            blocks.value.splice(blockIndex, 1);
            blocksChanged();
        };

        const cloneBlock = (block) => {
            blocks.value.push({
                ..._.cloneDeep(block),
                uuid: uuidv4(),
            });
            blocksChanged();
        }

        const saveCustomBlock = (element) => {
            sourceBlockTypeElement.value = element;
            showBlockSaveModal.value = true;
        }

        const sourceBlockTypeElement = ref(null);
        const showBlockSaveModal = ref(false);

        return {
            blocks,
            openBlockTypeModal,
            openBlockPreviewModal,
            deleteBlock,
            editBlock,
            cloneBlock,
            saveCustomBlock,
            sourceBlockTypeElement,
            showBlockSaveModal
        }
    }
}
</script>

<style scoped>

</style>
